import React, { useState, memo, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Spinner,
  Alert,
  CardHeader,
} from 'reactstrap';
import classnames from 'classnames';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';

import { useDetailQuery, useUpdatePatchMutation } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';

import { ShopFormValues, DayOfWeek, ShopBusinessHour } from 'components/Types/Tenant/shop.type';

// Tabs
import ContactLocationTab from './EditShopTabs/ContactLocationTab';
import SocialLinksTab from './EditShopTabs/SocialLinksTab';
import BusinessHoursTab from './EditShopTabs/BusinessHoursTab';
import MediaTab from './EditShopTabs/MediaTab';
import SeoSettingsTab from './EditShopTabs/SeoSettingsTab';
import ShopSettingsTab from './EditShopTabs/ShopSettingsTab';
import { shopValidationSchema } from 'components/Validations/shopValidationSchema';

const tabs = [
  { id: 'contact-location', label: 'Contact & Location' },
  // Uncomment if needed later
  // { id: 'social-links', label: 'Social Links' },
  // { id: 'business-hours', label: 'Business Hours' },
  { id: 'media', label: 'Media' },
  { id: 'seo-settings', label: 'SEO Settings' },
  { id: 'shop-settings', label: 'Shop Settings' },
];

const EditShopPage: React.FC<{ shopId: string }> = ({ shopId = '' }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Default tab fallback if none provided
  const defaultTab = 'contact-location';

  // Get tab from URL or fallback
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string>(
    urlTab && tabs.find((t) => t.id === urlTab) ? urlTab : defaultTab,
  );

  // Sync URL param with tab changes
  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setSearchParams({ tab });
    }
  };

  // Fetch shop data
  const {
    data: fetchedShop,
    isLoading,
    isError,
  } = useDetailQuery<ShopFormValues>(API_ENDPOINTS.TENANT.SHOPS.WITH_ID, {
    id: shopId,
  });

  const { mutate: updateShop, isPending: updating } = useUpdatePatchMutation(
    API_ENDPOINTS.TENANT.SHOPS.PATCH,
  );

  const allDays: ShopBusinessHour[] = Object.values(DayOfWeek)
    .filter((v) => typeof v === 'number')
    .map(
      (day) =>
        ({
          day: day as DayOfWeek,
          openTime: '00:00',
          closeTime: '00:00',
          isClosed: false,
        } as ShopBusinessHour),
    );

  const formik = useFormik<ShopFormValues>({
    initialValues: {
      name: fetchedShop?.name || '',
      slug: fetchedShop?.slug || '',
      city: fetchedShop?.city || '',
      state: fetchedShop?.state || '',
      country: fetchedShop?.country || '',
      streetAddress: fetchedShop?.streetAddress || '',
      contactNumber: fetchedShop?.contactNumber || '',
      description: fetchedShop?.description || '',
      logoUrl: fetchedShop?.logoUrl || '',
      coverImageUrl: fetchedShop?.coverImageUrl || '',
      notificationEmail: fetchedShop?.notificationEmail || null,
      enableNotification: fetchedShop?.enableNotification ?? false,
      zipCode: fetchedShop?.zipCode || '',
      metaDescription: fetchedShop?.metaDescription || '',
      metaKeywords: fetchedShop?.metaKeywords || '',
      isActive: fetchedShop?.isActive ?? true,
      socialProfiles: fetchedShop?.socialProfiles ?? [],
      businessHours: fetchedShop?.businessHours?.length ? fetchedShop.businessHours : allDays,
    },
    enableReinitialize: true,
    validationSchema: shopValidationSchema,
    onSubmit: (values) => {
      if (!shopId) return;
      updateShop(
        { data: values, params: { id: shopId } },
        {
          onSuccess: () => {
            console.log('Updated successfully');
          },
          onError: (error) => {
            console.error('Update error:', error);
          },
        },
      );
    },
  });

  const addSocialProfile = () => {
    formik.setFieldValue('socialProfiles', [
      ...formik.values.socialProfiles,
      { id: crypto.randomUUID(), platform: '', url: '' },
    ]);
  };

  const removeSocialProfile = (index: number) => {
    formik.setFieldValue(
      'socialProfiles',
      formik.values.socialProfiles.filter((_, i) => i !== index),
    );
  };

  const addBusinessHour = () => {
    formik.setFieldValue('businessHours', [
      ...formik.values.businessHours,
      {
        id: 0,
        shopId: shopId || '',
        day: DayOfWeek.Sunday,
        openTime: '09:00',
        closeTime: '17:00',
        isClosed: false,
      },
    ]);
  };

  const removeBusinessHour = (index: number) => {
    formik.setFieldValue(
      'businessHours',
      formik.values.businessHours.filter((_, i) => i !== index),
    );
  };

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner color="primary" />
      </Container>
    );
  }

  if (isError || !fetchedShop) {
    return (
      <Container className="py-5">
        <Alert color="danger" className="text-center">
          Failed to load shop details.
        </Alert>
      </Container>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col lg={12}>
        <Card className="shadow-sm">
          <CardHeader>
            <h2 className="text-center fw-bold fs-2">Edit Shop: {fetchedShop.name}</h2>
          </CardHeader>
          {/* render validation errors here */}
          <CardBody>
            {Object.keys(formik.errors).length > 0 && (
              <div className="mb-4 p-3 border border-danger rounded">
                <h6 className="text-danger mb-3">Please fix the following errors:</h6>
                <ul
                  className="mb-0 text-danger"
                  style={{ listStyleType: 'disc', paddingLeft: '1.25rem' }}
                >
                  {Object.entries(formik.errors).map(([field, error]) => (
                    <li key={field} className="mb-1">
                      {Array.isArray(error) ? error.join(', ') : error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>

          <Nav tabs className="nav-tabs nav-tabs-custom justify-content-center pt-2">
            {tabs.map((tab) => (
              <NavItem key={tab.id}>
                <NavLink
                  href="#"
                  className={classnames({ active: activeTab === tab.id })}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTab(tab.id);
                  }}
                >
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <CardBody>
            <form onSubmit={formik.handleSubmit} noValidate>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="contact-location">
                  <ContactLocationTab formik={formik} />
                </TabPane>

                {/* Uncomment if you want these tabs later */}
                {/* <TabPane tabId="social-links">
                  <SocialLinksTab
                    formik={formik}
                    addSocialProfile={addSocialProfile}
                    removeSocialProfile={removeSocialProfile}
                  />
                </TabPane> */}

                {/* <TabPane tabId="business-hours">
                  <BusinessHoursTab
                    formik={formik}
                    addBusinessHour={addBusinessHour}
                    removeBusinessHour={removeBusinessHour}
                  />
                </TabPane> */}

                <TabPane tabId="media">
                  <MediaTab formik={formik} />
                </TabPane>
                <TabPane tabId="seo-settings">
                  <SeoSettingsTab formik={formik} />
                </TabPane>
                <TabPane tabId="shop-settings">
                  <ShopSettingsTab formik={formik} />
                </TabPane>
              </TabContent>
              <div className="d-flex justify-content-center mt-4">
                <Button color="primary" type="submit" disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default memo(EditShopPage);
